<script lang="ts" context="module">
	export type ChartLegendEntry = {
		id: string;
		label: string;
		color: string;
		valueLabel?: string;
	};
</script>

<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	import { ButtonV2, Text } from '@Shelly/shelly-design-system';
	import ChartTokenList from 'src/components/Chart/ChartTokenList.svelte';

	export let items: ChartLegendEntry[] = [];
	export let layout: 'row' | 'column' = 'row';
	export let interactive = false;
	export let hiddenItemIds: string[] = [];
	export let tokenized = false;
	export let wrapTokens = false;
	let className = '';
	export { className as class };

	const dispatch = createEventDispatcher<{ toggle: { id: string } }>();
</script>

{#if items.length}
	{#if tokenized}
		<ChartTokenList
			class={className}
			{items}
			mode={interactive ? 'toggle' : 'none'}
			selectedItemIds={items
				.filter((item) => !hiddenItemIds.includes(item.id))
				.map((item) => item.id)}
			wrap={wrapTokens}
			on:select={(event) => dispatch('toggle', event.detail)}
		/>
	{:else}
		<div
			class="em-chart-legend-row {className}"
			class:em-chart-legend-column={layout === 'column'}
			role="list"
		>
			{#each items as item (item.id)}
				{#if interactive}
					<div role="listitem">
						<ButtonV2
							size="s"
							variant="secondary"
							class="em-chart-legend-button"
							aria-pressed={!hiddenItemIds.includes(item.id)}
							aria-label={`${hiddenItemIds.includes(item.id) ? 'Show' : 'Hide'} ${item.label}`}
							on:click={() => dispatch('toggle', { id: item.id })}
						>
							<span
								class="em-chart-legend-item"
								class:em-chart-legend-item--hidden={hiddenItemIds.includes(item.id)}
							>
								<span class="em-chart-legend-dot" style={`background: ${item.color};`}
								></span>
								<Text styleType="miniature" class="text-600">
									{item.label}
									{#if item.valueLabel}
										<span class="text-color-muted">({item.valueLabel})</span>
									{/if}
								</Text>
							</span>
						</ButtonV2>
					</div>
				{:else}
					<div class="em-chart-legend-item" role="listitem">
						<span class="em-chart-legend-dot" style={`background: ${item.color};`}></span>
						<Text styleType="miniature" class="text-600">
							{item.label}
							{#if item.valueLabel}
								<span class="text-color-muted">({item.valueLabel})</span>
							{/if}
						</Text>
					</div>
				{/if}
			{/each}
		</div>
	{/if}
{/if}

<style>
	.em-chart-legend-row {
		display: flex;
		flex-wrap: wrap;
		justify-content: center;
		gap: var(--spacing-xs) var(--spacing-m);
		margin-top: var(--spacing-s);
		padding: 0 var(--spacing-xs) var(--spacing-s);
	}

	.em-chart-legend-item {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		min-width: 0;
	}

	.em-chart-legend-item--hidden {
		opacity: 0.45;
	}

	.em-chart-legend-row :global(.em-chart-legend-button) {
		min-width: 0;
	}

	.em-chart-legend-dot {
		width: 10px;
		height: 10px;
		border-radius: 2px;
		flex: 0 0 auto;
	}

	.em-chart-legend-column {
		flex-direction: column;
		align-items: flex-start;
		justify-content: flex-start;
		gap: var(--spacing-xs);
	}
</style>
